// Planches contact (outils de développement, aucune donnée de jeu) :
//   /?sheet=classes             les 150 classes (et le petit tank) côte à côte, style Atelier ;
//   /?sheet=variants&family=C01 une famille : 10 classes × 5 styles = 50 des 750 variantes.
import { useEffect, useRef } from 'react';
import { Application, Container, Graphics, Text } from 'pixi.js';
import { CLASSES, type ClassNode } from '../../../shared/catalog/classes';
import { STYLES, STYLE_CODE, STYLE_DEFS } from '../../../shared/catalog/variants';
import { ACCENT_SELF, ART_R } from '../game/render/art';
import { drawTankParts } from '../game/render/variants';

function tank(c: ClassNode, code: number, x: number, y: number, size: number): Container {
  const root = new Container();
  const parts = { hull: new Graphics(), turret: new Graphics(), auto: new Graphics() };
  drawTankParts(parts, c, code, ACCENT_SELF);
  parts.hull.rotation = -Math.PI / 2;
  parts.turret.rotation = -Math.PI / 4;
  parts.auto.rotation = Math.PI / 3;
  root.addChild(parts.hull, parts.turret, parts.auto);
  root.position.set(x, y);
  root.scale.set(size / ART_R);
  return root;
}

function label(text: string, x: number, y: number, size = 11): Text {
  const t = new Text({ text, style: { fontFamily: 'system-ui', fontSize: size, fill: 0xdfe6ee, align: 'center' } });
  t.anchor.set(0.5, 0);
  t.position.set(x, y);
  return t;
}

function useSheet(draw: (app: Application) => void, width: number, height: number) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const host = ref.current;
    if (!host) return;
    const app = new Application();
    let destroyed = false;
    void app.init({ width, height, background: 0x151a20, antialias: true, resolution: 1 }).then(() => {
      if (destroyed) return;
      host.appendChild(app.canvas);
      draw(app);
      app.render();
    });
    return () => {
      destroyed = true;
      app.destroy({ removeView: true });
    };
  }, []);
  return ref;
}

export function ClassSheet() {
  const cell = 150;
  const cols = 11;
  const rows = Math.ceil(CLASSES.length / cols);
  const ref = useSheet(
    (app) =>
      CLASSES.forEach((c, i) => {
        const x = (i % cols) * cell + cell / 2;
        const y = Math.floor(i / cols) * cell + cell / 2 - 10;
        app.stage.addChild(tank(c, STYLE_CODE.atelier, x, y, 28), label(`${c.id}\n${c.name}`, x, y + 44));
      }),
    cols * cell,
    rows * cell,
  );
  return <div ref={ref} style={{ overflow: 'auto', height: '100%' }} data-testid="class-sheet" />;
}

export function VariantSheet({ family }: { family: string }) {
  const list = CLASSES.filter((c) => c.id.startsWith(`${family}-`));
  const cell = 175;
  const left = 170;
  const top = 46;
  const ref = useSheet(
    (app) => {
      app.stage.addChild(label(`${list[0]?.family ?? family} — ${list.length} classes × ${STYLES.length} styles`, (left + STYLES.length * cell) / 2, 8, 15));
      STYLES.forEach((s, j) => app.stage.addChild(label(STYLE_DEFS[s].name, left + j * cell + cell / 2, 28, 13)));
      list.forEach((c, i) => {
        const y = top + i * cell + cell / 2 - 8;
        const name = label(`${c.id}\n${c.name}\nniv. ${c.level}`, left / 2, y - 22, 12);
        app.stage.addChild(name);
        STYLES.forEach((s, j) => app.stage.addChild(tank(c, STYLE_CODE[s], left + j * cell + cell / 2, y, 46)));
      });
    },
    left + STYLES.length * cell,
    top + list.length * cell,
  );
  return <div ref={ref} style={{ overflow: 'auto', height: '100%' }} data-testid="variant-sheet" />;
}
